// Écran du proche : envoyé à, reçu à, pris à (parcours « Lui », étape 3). Pas
// de réglage de la pilule depuis son téléphone, ni de texte de notice.
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';

import { Chargement, ErreurReseau, Lignes, Texte, Titre } from '../components/base';
import type { JourComplet } from '../lib/api';
import { dateLongue, heure } from '../lib/format';
import { useAujourdhui } from '../lib/requetes';

function Ligne({ libelle, valeur }: Readonly<{ libelle: string; valeur: string }>) {
  return (
    <li className="flex justify-between gap-4 py-3">
      <span className="text-secondaire">{libelle}</span>
      <span className="text-right">{valeur}</span>
    </li>
  );
}

/** « Pas reçu » seulement si le téléphone accuse d'habitude ; sinon, on ne sait pas (#25). */
function recu(
  j: JourComplet,
  h: (d: Date | null) => string,
  t: ReturnType<typeof useTranslation>['t'],
): string {
  if (j.envois.reception === 'non-recu') return t('accuse.pasRecu');
  if (j.envois.reception === 'sans-accuse') return t('accuse.sansAccuse');
  return h(j.envois.premierRecuLe);
}

function Etat({ j, fuseau }: Readonly<{ j: JourComplet; fuseau: string }>) {
  const { t } = useTranslation();
  const h = (d: Date | null) => (d ? heure(d, fuseau) : t('proche.pasEncore'));
  if (j.jour.genre === 'arret') return <Texte>{t('proche.arret')}</Texte>;
  return (
    <Lignes label={j.cible === 'hier' ? t('proche.etatHier') : t('proche.etat')}>
      <Ligne libelle={t('proche.prevu')} valeur={heure(j.heure, fuseau)} />
      <Ligne libelle={t('proche.envoye')} valeur={h(j.envois.premierEnvoiLe)} />
      <Ligne libelle={t('proche.recu')} valeur={recu(j, h, t)} />
      <Ligne
        libelle={t('proche.pris')}
        valeur={j.prise ? heure(j.prise.le, fuseau) : t('proche.pasEncore')}
      />
      <Ligne libelle={t('proche.relances')} valeur={String(Math.max(0, j.envois.nombre - 1))} />
    </Lignes>
  );
}

/** Après l'heure, sans « Prise » : dire au proche ce qu'il peut faire (#14). */
function QueFaire({ j }: Readonly<{ j: JourComplet }>) {
  const { t } = useTranslation();
  if (j.prise || j.jour.genre === 'arret' || new Date() < j.heure) return null;
  return <Texte>{t('proche.queFaire')}</Texte>;
}

export function AujourdhuiProche({
  fuseau,
  prevenir,
  aChaquePrise,
}: Readonly<{ fuseau: string; prevenir: boolean; aChaquePrise: boolean }>) {
  const { t } = useTranslation();
  const { data, isError, refetch } = useAujourdhui();
  if (isError) return <ErreurReseau onReessayer={() => void refetch()} />;
  if (!data) return <Chargement />;
  if (!data.reglagesComplets) return <Texte>{t('proche.reglagesAFaire')}</Texte>;
  return (
    <section>
      <p className="text-secondaire text-sm">
        {data.cible === 'hier'
          ? t('jour.soireeHier', { date: dateLongue(data.date) })
          : t('jour.soiree', { date: dateLongue(data.date) })}
      </p>
      <Titre>{data.prise ? t('proche.confirme') : t('proche.nonConfirme')}</Titre>
      <Etat j={data} fuseau={fuseau} />
      <QueFaire j={data} />
      <Texte discret>{prevenir ? t('proche.prevenu') : t('proche.alertesCoupees')}</Texte>
      {prevenir && aChaquePrise && <Texte discret>{t('proche.aChaquePrise')}</Texte>}
      <p className="mt-6">
        <Link
          to="/messages"
          className="text-accent inline-flex min-h-11 items-center underline underline-offset-4"
        >
          {t('proche.changerConfirmation')}
        </Link>
      </p>
    </section>
  );
}
